import type { DeactivateOtherOutcomesQueryMode, SuperAdminFlags, SuperAdminProviderStatuses } from '../@types/api';

export type NumericField = 'ftbResolveBufferMaxDelayedJobs' | 'ftbResolveBufferDelayS' | 'resolveBettingSlipsBatchSize';
export type BooleanField = Exclude<keyof SuperAdminFlags, NumericField | 'deactivateOtherOutcomesQueryMode'>;

type SettingDefinition<Field extends keyof SuperAdminFlags> = {
  field: Field;
  label: string;
  description: string;
  group: 'Platform' | 'Risk management' | 'FTB consumer' | 'Redis controls';
};

type ProviderStatusDefinition = {
  field: keyof SuperAdminProviderStatuses;
  label: string;
};

export const REDIS_FLAG_KEYS: Record<keyof SuperAdminFlags, string> = {
  feedRunning: 'feedRunning',
  casinoRunning: 'casinoRunning',
  manualTemplateSwitch: 'feed:manual-template-switch',
  includeNuMessages: 'ff:include:nu',
  snapshotInProgress: 'snapshot:inProgress',
  manualSnapshotCooldown: 'bo:templates:request-snapshot:cooldown',
  providerRunningSM: 'providerRunning:SM',
  providerRunningVB: 'providerRunning:VB',
  providerRunningB3: 'providerRunning:B3',
  providerRunningXB: 'providerRunning:XB',
  resolveBettingSlipsBatchSize: 'RESOLVE_BETTING_SLIPS_BATCH_SIZE',
  ftbResolveBufferMaxDelayedJobs: 'FTB_RESOLVE_BUFFER_MAX_DELAYED_JOBS',
  ftbResolveBufferDelayS: 'FTB_RESOLVE_BUFFER_DELAY_S',
  ftbTicketValidation: 'ff:ftb_ticket_validation',
  ftbSettlementResolveBuffer: 'ff:ftb_settlement_resolve_buffer',
  feedKillSwitch: 'feed:kill-switch',
  deactivateOtherOutcomes: 'ff:deactivate_other_outcomes',
  riskValidation: 'ff:risk',
  disableBetBuilderLiability: 'ff:risk:disable-bet-builder-liability',
  sportLiabilityByBetEvent: 'ff:risk:sport-liability-by-bet-event',
  ftbCompetitionUpsertCache: 'ff:ftb_competition_upsert_cache',
  ftbMarketUpsertCache: 'ff:ftb_market_upsert_cache',
  ftbPreferParticipantName: 'ff:ftb_prefer_participant_name_over_provider_name',
  insertMarketsWithoutLock: 'feed:without-xact-lock:markets',
  deactivateOtherOutcomesQueryMode: 'ff:ftb_deactivate_other_outcomes_query_mode',
};

export const BOOLEAN_SETTINGS: SettingDefinition<BooleanField>[] = [
  {
    group: 'Platform',
    field: 'feedRunning',
    label: 'Feed Running (Maintenance Mode)',
    description: 'Turn the main feed on or off. When this is off, the client app goes into maintenance mode.',
  },
  {
    group: 'Platform',
    field: 'casinoRunning',
    label: 'Casino Running (Maintenance Mode)',
    description: 'Turn the casino on or off. When this is off, the client app shows the casino maintenance screen.',
  },
  {
    group: 'FTB consumer',
    field: 'manualTemplateSwitch',
    label: 'Manual Template Switch',
    description:
      'Use manual template mode. When on, automatic failover is disabled. When off, provider signals may switch templates.',
  },
  {
    group: 'FTB consumer',
    field: 'includeNuMessages',
    label: 'Include NU Messages',
    description:
      'Process NU outcomes during normal feed updates. Snapshot mode includes NU outcomes regardless of this flag.',
  },
  {
    group: 'Redis controls',
    field: 'snapshotInProgress',
    label: 'Snapshot In Progress',
    description:
      'Override the consumer snapshot state. On includes NU outcomes and expires after one hour. Off does not cancel the provider snapshot or clear fixture tracking. Feed signals can change this state.',
  },
  {
    group: 'Redis controls',
    field: 'manualSnapshotCooldown',
    label: 'Manual Snapshot Request Cooldown',
    description:
      'Block manual snapshot requests temporarily. On expires after three minutes. Off clears the cooldown gate; it does not request or cancel a snapshot.',
  },
  {
    group: 'Redis controls',
    field: 'providerRunningSM',
    label: 'SM Provider Running Override',
    description:
      'Override the stored SM provider status. Provider-up/down signals can overwrite this value; it does not switch templates or start the provider.',
  },
  {
    group: 'Redis controls',
    field: 'providerRunningVB',
    label: 'VB Provider Running Override',
    description:
      'Override the stored VB provider status. Provider-up/down signals can overwrite this value; it does not switch templates or start the provider.',
  },
  {
    group: 'Redis controls',
    field: 'providerRunningB3',
    label: 'B3 Provider Running Override',
    description:
      'Override the stored B3 provider status. Provider-up/down signals can overwrite this value; it does not switch templates or start the provider.',
  },
  {
    group: 'Redis controls',
    field: 'providerRunningXB',
    label: 'XB Provider Running Override',
    description:
      'Override the stored XB provider status. Provider-up/down signals can overwrite this value; it does not switch templates or start the provider.',
  },
  {
    group: 'FTB consumer',
    field: 'ftbTicketValidation',
    label: 'FTB Ticket Validation',
    description: 'Enable ticket validation on the Feed The Bet side before tickets are processed.',
  },
  {
    group: 'FTB consumer',
    field: 'ftbSettlementResolveBuffer',
    label: 'FTB Settlement Resolve Buffer',
    description: 'Delay settlement resolving through a buffer instead of processing settlements immediately.',
  },
  {
    group: 'FTB consumer',
    field: 'feedKillSwitch',
    label: 'Feed Kill Switch',
    description: 'Pause FTB fixture processing. Feed recovery also controls this switch.',
  },
  {
    group: 'FTB consumer',
    field: 'deactivateOtherOutcomes',
    label: 'Deactivate Other Outcomes',
    description: 'Deactivate only FTB outcomes missing from the latest fixture update.',
  },
  {
    group: 'Risk management',
    field: 'riskValidation',
    label: 'Risk Validation',
    description: 'Run risk checks during bet placement. Turning this off bypasses risk validation.',
  },
  {
    group: 'Risk management',
    field: 'disableBetBuilderLiability',
    label: 'Disable Bet Builder Liability',
    description: 'When enabled, excludes Bet Builder bets from liability calculations.',
  },
  {
    group: 'Risk management',
    field: 'sportLiabilityByBetEvent',
    label: 'Sport Liability by Bet Event',
    description:
      'Use the stored bet event for sport liability. Enable only after the bet-event backfill reports no missing or mismatched records and the foreign key is validated.',
  },
  {
    group: 'FTB consumer',
    field: 'ftbCompetitionUpsertCache',
    label: 'FTB Competition Upsert Cache',
    description: 'Cache unchanged competition data to reduce repeated database writes.',
  },
  {
    group: 'FTB consumer',
    field: 'ftbMarketUpsertCache',
    label: 'FTB Market Upsert Cache',
    description: 'Cache market IDs per event to avoid inserting markets that are already stored.',
  },
  {
    group: 'FTB consumer',
    field: 'ftbPreferParticipantName',
    label: 'Prefer Participant Names',
    description: 'Prefer participant names over provider names when processing FTB fixtures and participants.',
  },
  {
    group: 'FTB consumer',
    field: 'insertMarketsWithoutLock',
    label: 'Insert Markets Without Transaction Lock',
    description: 'Bypass the advisory transaction lock during market insertion.',
  },
];

export const FLAG_GROUPS = ['Platform', 'Risk management', 'FTB consumer', 'Redis controls'] as const;

export const QUERY_MODE_OPTIONS: { value: DeactivateOtherOutcomesQueryMode; label: string }[] = [
  { value: 'legacy', label: 'Legacy' },
  { value: 'direct_update', label: 'Direct update' },
  { value: 'direct_skip_locked', label: 'Direct update (skip locked)' },
];

export const NUMERIC_SETTINGS: SettingDefinition<NumericField>[] = [
  {
    group: 'FTB consumer',
    field: 'ftbResolveBufferMaxDelayedJobs',
    label: 'FTB Resolve Buffer Max Delayed Jobs',
    description: 'Maximum delayed jobs allowed in the FTB resolve buffer queue.',
  },
  {
    group: 'FTB consumer',
    field: 'ftbResolveBufferDelayS',
    label: 'FTB Resolve Buffer Delay (s)',
    description: 'Delay in seconds applied to FTB resolve buffer jobs.',
  },
  {
    group: 'Redis controls',
    field: 'resolveBettingSlipsBatchSize',
    label: 'Betting Slip Resolution Batch Size',
    description: 'Maximum betting slips loaded per resolution batch. Positive whole number; default 250.',
  },
];

export const PROVIDER_STATUS_SETTINGS: ProviderStatusDefinition[] = [
  {
    field: 'providerRunningSM',
    label: 'SM',
  },
  {
    field: 'providerRunningVB',
    label: 'VB',
  },
  {
    field: 'providerRunningB3',
    label: 'B3',
  },
  {
    field: 'providerRunningXB',
    label: 'XB',
  },
];

const FEED_CONTROL_FIELDS: readonly BooleanField[] = [
  'feedRunning',
  'manualTemplateSwitch',
  'includeNuMessages',
  'snapshotInProgress',
  'manualSnapshotCooldown',
  'providerRunningSM',
  'providerRunningVB',
  'providerRunningB3',
  'providerRunningXB',
  'ftbTicketValidation',
  'ftbSettlementResolveBuffer',
  'feedKillSwitch',
  'deactivateOtherOutcomes',
  'ftbCompetitionUpsertCache',
  'ftbMarketUpsertCache',
  'ftbPreferParticipantName',
  'insertMarketsWithoutLock',
];

export const FEED_CONTROL_SETTINGS = BOOLEAN_SETTINGS.filter(({ field }) => FEED_CONTROL_FIELDS.includes(field));
